<template>
  <div class="min-h-screen bg-gray-100">
    <div class="py-10">
      <main>
        <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
          <div>
            <!-- Search Section -->
            <div class="card mb-8">
              <div class="max-w-3xl mx-auto">
                <form @submit.prevent="analyzeKeyword">
                  <div>
                    <label for="keyword" class="block text-lg font-medium text-gray-900 dark:text-white mb-4">
                      Enter a keyword to analyze
                    </label>
                    <!-- Location and Language Selectors -->
                    <div class="grid grid-cols-2 gap-4 mb-4">
                      <!-- Location Selector -->
                      <div>
                        <label for="location" class="block text-sm font-medium text-gray-700 mb-1">Location</label>
                        <select
                          id="location"
                          v-model="locationStore.selectedLocation"
                          class="block w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-base"
                          :disabled="locationStore.loading"
                        >
                          <option v-if="locationStore.loading" value="">Loading locations...</option>
                          <option v-else-if="locationStore.error" value="">Error loading locations</option>
                          <template v-else>
                            <optgroup label="Countries">
                              <option
                                v-for="location in locationStore.locations.filter(l => l.is_country)"
                                :key="location.location_code"
                                :value="location"
                              >
                                {{ location.location_name }}
                              </option>
                            </optgroup>
                            <optgroup label="Regions">
                              <option
                                v-for="location in locationStore.locations.filter(l => !l.is_country)"
                                :key="location.location_code"
                                :value="location"
                              >
                                {{ location.location_name }}
                              </option>
                            </optgroup>
                          </template>
                        </select>
                      </div>
                      <!-- Language Selector -->
                      <div>
                        <label for="language" class="block text-sm font-medium text-gray-700 mb-1">Language</label>
                        <select
                          id="language"
                          v-model="languageStore.selectedLanguage"
                          class="block w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-base"
                          :disabled="languageStore.loading"
                        >
                          <option v-if="languageStore.loading" value="">Loading languages...</option>
                          <option v-else-if="languageStore.error" value="">Error loading languages</option>
                          <template v-else>
                            <option
                              v-for="language in languageStore.languages"
                              :key="language.language_code"
                              :value="language"
                            >
                              {{ language.language_name }}
                            </option>
                          </template>
                        </select>
                      </div>
                    </div>
                    <!-- Keyword Input -->
                    <div class="flex rounded-md shadow-sm">
                      <input
                        type="text"
                        name="keyword"
                        id="keyword"
                        v-model="searchTerm"
                        class="flex-1 min-w-0 block w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-base"
                        placeholder="Enter a keyword..."
                      />
                      <button
                        type="submit"
                        :disabled="loading"
                        class="btn btn-primary ml-4"
                      >
                        <span v-if="!loading">Analyze</span>
                        <svg v-else class="icon animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>

            <!-- Results Section -->
            <div class="space-y-8">
              <!-- Error Alert -->
              <div v-if="error" class="rounded-lg bg-red-50 dark:bg-red-900/20 p-4">
                <div class="flex">
                  <div class="flex-shrink-0">
                    <svg class="icon text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                    </svg>
                  </div>
                  <div class="ml-3">
                    <p class="text-sm font-medium text-red-800 dark:text-red-200">{{ error }}</p>
                  </div>
                </div>
              </div>

              <!-- Seed Keyword Analysis -->
              <SeedKeywordAnalysis v-if="seedKeyword" :keyword="seedKeyword" />

              <!-- Related Keywords -->
              <RelatedKeywords 
                v-if="relatedKeywords.length" 
                :keywords="relatedKeywords"
              />

              <!-- Blog Ideas Popup -->
              <BlogIdeasPopup
                v-if="showBlogIdeasPopup"
                :show="showBlogIdeasPopup"
                :keyword="selectedKeyword"
                :ideas="blogIdeas"
                :loading="loading"
                :language-code="languageStore.selectedLanguage?.language_code"
                v-model:show-faqs="showFaqs"
                v-model:show-takeaways="showTakeaways"
                v-model:num-faqs="numFaqs"
                v-model:num-takeaways="numTakeaways"
                v-model:content-length="contentLength"
                @close="closeBlogIdeasPopup"
                @generate="generateBlogIdeas"
                @write="writeArticle"
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useKeywordStore } from '../stores/keyword'
import { useLocationStore } from '../stores/location'
import { useAuthStore } from '../stores/auth'
import { useLanguageStore } from '../stores/language'
import { storeToRefs } from 'pinia'
import SeedKeywordAnalysis from '../components/SeedKeywordAnalysis.vue'
import RelatedKeywords from '../components/RelatedKeywords.vue'
import BlogIdeasPopup from '../components/BlogIdeasPopup.vue'

const router = useRouter()
const keywordStore = useKeywordStore()
const locationStore = useLocationStore()
const authStore = useAuthStore()
const languageStore = useLanguageStore()
const searchTerm = ref('')
const loading = ref(false)
const error = ref(null)

// FAQ and Takeaway state
const showFaqs = ref(false)
const showTakeaways = ref(false)
const numFaqs = ref(3)
const numTakeaways = ref(3)

// Blog Ideas Popup state
const showBlogIdeasPopup = ref(false)
const selectedKeyword = ref(null)
const blogIdeas = ref([])
const contentLength = ref('medium')

const { seedKeyword, relatedKeywords } = storeToRefs(keywordStore)

onMounted(() => {
  locationStore.fetchLocations()
  languageStore.fetchLanguages()
})

onUnmounted(() => {
  keywordStore.clearKeywords()
})

async function analyzeKeyword() {
  if (!searchTerm.value) return
  loading.value = true
  error.value = null
  try {
    const locationCode = locationStore.selectedLocation?.location_code || 2840 // Default to US
    const languageCode = languageStore.selectedLanguage?.language_code
    
    // Clear existing keywords before new search
    keywordStore.clearKeywords()
    
    // This will fetch from API and save to database
    await keywordStore.analyzeKeyword(searchTerm.value, locationCode, languageCode)
    searchTerm.value = '' // Clear the input after successful search
  } catch (e) {
    error.value = e.message
  } finally {
    loading.value = false
  }
}

function openBlogIdeasPopup(keyword) {
  selectedKeyword.value = keyword
  showBlogIdeasPopup.value = true
}

function closeBlogIdeasPopup() {
  showBlogIdeasPopup.value = false
  selectedKeyword.value = null
  blogIdeas.value = []
}

async function generateBlogIdeas({ keyword, contentType, languageCode }) {
  try {
    loading.value = true
    // Your existing blog ideas generation logic
    // ...
  } catch (error) {
    console.error('Error generating blog ideas:', error)
    error.value = error.message
  } finally {
    loading.value = false
  }
}

async function writeArticle(articleData) {
  try {
    const params = {
      ...articleData,
      contentLength: contentLength.value
    }
    await openAIStore.writeArticle(params)
    showBlogIdeasPopup.value = false
  } catch (error) {
    console.error('Error writing article:', error)
    error.value = error.message
  }
}
</script>

<style scoped>
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

.card {
  background-color: #fff;
  padding: 1.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.card.mb-8 {
  margin-bottom: 2rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.btn-primary {
  background-color: #4CAF50;
  color: white;
}

.error {
  color: #f44336;
  padding: 1rem;
  margin-bottom: 1rem;
  background-color: #ffebee;
  border-radius: 4px;
}

.icon {
  width: 1.5rem;
  height: 1.5rem;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
